import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { SharedModule } from '../../../core/shared.module';
import { CurrentSort } from '../../../interfaces/table.interface';
import { PaginationComponent } from '../pagination/pagination.component';
import { FiltroService } from '../../../service/filtro.service';
import { BehaviorSubject, Subscription, switchMap } from 'rxjs';
import { LoaderService } from '../../../service/loader.service';
import { DelegaTimesheet } from '../../../interfaces/delega.interface';
import { DelegheService } from '../../../service/deleghe.service';
import { FiltroDeleghe, filtroDelegheVuoto } from '../../../interfaces/filtro-deleghe.interface';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-tabella-storico-deleghe',
  standalone: true,
  templateUrl: './tabella-storico-deleghe.component.html',
  imports: [SharedModule, PaginationComponent, MatTooltipModule],
})
export class TabellaStoricoDelegheComponent {
  @Output() disattivaEvent = new EventEmitter<DelegaTimesheet>();

  @Input() elencoCount: number = 0;
  @Input() idAnagraficaTimesheetCorrente: number = 0;

  private _dataSource!: DelegaTimesheet[];
  @Input() set dataSource(value: DelegaTimesheet[]) {
    if (value) {
      this._dataSource = value;
      this.componentLoaded = true;
    }
  }

  get dataSource() {
    return this._dataSource;
  }

  pageSubscriptions: Subscription = new Subscription();
  currentSort: CurrentSort = {
    flagAsc: true,
    orderBy: '',
  };
  totalPages: number = 0;
  currentPage: number = 0;
  pageOffset: number = 10;
  filtro: FiltroDeleghe = { ...filtroDelegheVuoto };
  form: any = {};
  componentLoaded: boolean = false;
  private readonly pageSubject = new BehaviorSubject<number>(0);

  constructor(
    private filtroService: FiltroService,
    private loaderService: LoaderService,
    private delegheService: DelegheService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.filtroService.ultimoFiltroDeleghe.subscribe((filtro) => {
      this.form = filtro;
    });
    if (this.filtroService.ultimaPaginaDelegheInfo.pageOffset) {
      this.pageOffset = this.filtroService.ultimaPaginaDelegheInfo.pageOffset;
    }
    if (this.filtroService.ultimaPaginaDelegheInfo.currentPage) {
      this.currentPage = this.filtroService.ultimaPaginaDelegheInfo.currentPage;
    }
    this.totalPages = Math.ceil(this.elencoCount / this.pageOffset);
  }

  handleSearchEvent(event: any): void {
    this.form = event;
    this.getPage();
  }

  exportDataToExcel(): void {
    this.loaderService.show();
    const exportFiltro = { ...this.filtro, flagExport: true };
    this.pageSubscriptions.add(
      this.delegheService
        .getStoricoDeleghe(exportFiltro)
        .subscribe({
          next: (result) => {
            if (result) {
              const blob = new Blob([result], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'Export_Deleghe.xlsx';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }
          },
          complete: () => this.loaderService.hide(),
          error: () => this.loaderService.hide(),
        })
    );
  }

  onDisattiva(delega: DelegaTimesheet): void {
    this.disattivaEvent.emit(delega);
  }

  getCaretClass(
    orderBy: string,
    flagAsc: boolean
  ):
    | 'bi-caret-up'
    | 'bi-caret-up-fill'
    | 'bi-caret-down'
    | 'bi-caret-down-fill'
    | undefined {
    if (
      orderBy !== this.currentSort.orderBy ||
      (orderBy === this.currentSort.orderBy &&
        flagAsc !== this.currentSort.flagAsc)
    ) {
      return flagAsc ? 'bi-caret-down' : 'bi-caret-up';
    }
    return flagAsc ? 'bi-caret-down-fill' : 'bi-caret-up-fill';
  }

  sort(orderBy: string) {
    if (orderBy === this.currentSort.orderBy) {
      this.currentSort.flagAsc = !this.currentSort.flagAsc;
    } else {
      this.currentSort = {
        orderBy,
        flagAsc: true,
      };
    }
    this.getPage();
  }

  getPage(page: number = 0): void {
    this.componentLoaded = false;
    this.currentPage = page;
    this.filtroService.setUltimaPaginaDelegheInfo({
      currentPage: this.currentPage,
      pageOffset: this.pageOffset,
    });

    const convertEmptyStringsToNull = (obj: any) => {
      Object.keys(obj).forEach((key) => {
        if (obj[key] === '') {
          obj[key] = null;
        }
      });
      return obj;
    };

    const formValues = convertEmptyStringsToNull(this.form);
    this.filtro = {
      anno: formValues.anno,
      mese: formValues.mese,
      delegante: formValues.delegante,
      delegato: formValues.delegato,
      flagExport: false,
      pageNumber: page + 1,
      pageSize: this.pageOffset,
      flagAsc: this.currentSort.flagAsc,
      orderBy: this.currentSort.orderBy,
    };
    this.filtroService.setUltimoFiltroDeleghe(this.filtro);
    this.loaderService.show();
    this.pageSubscriptions.add(
      this.delegheService
        .getStoricoDelegheCount(this.filtro)
        .pipe(
          switchMap((count: number) => {
            this.elencoCount = count;
            this.totalPages = Math.ceil(this.elencoCount / this.pageOffset);
            return this.delegheService.getStoricoDeleghe(this.filtro);
          })
        )
        .subscribe({
          next: (data: DelegaTimesheet[]) => {
            this.dataSource = data;
            this.cd.detectChanges();
          },
          complete: () => this.loaderService.hide(),
          error: () => this.loaderService.hide(),
        })
    );
    this.pageSubject.next(page);
    this.componentLoaded = true;
  }

  changerEvent(value: number): void {
    this.pageOffset = value;
    this.totalPages = Math.ceil(this.elencoCount / this.pageOffset);
    this.currentPage = Math.min(this.currentPage, this.totalPages - 1);
    this.getPage(this.currentPage);
    this.pageSubject.next(this.currentPage);
  }

  ngOnDestroy(): void {
    this.pageSubscriptions.unsubscribe();
  }
}
